/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { alpha } from '@mui/material/styles'
import type { Theme } from '@mui/material/styles'
import { theme } from '@app/styles/theme'

export const dropPlaceholderStyles = (theme: Theme, isDragOver: boolean) => css`
  min-height: 320px;
  height: 100%;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 24px;
  background: ${isDragOver
    ? alpha(theme.palette.primary.main, 0.05)
    : alpha(theme.palette.white.main, 1)};
  border: 1px solid
    ${isDragOver
      ? alpha(theme.palette.primary.main, 0.18)
      : alpha(theme.palette.text.primary, 0.06)};
  transition:
    background-color 160ms ease,
    border-color 160ms ease;
`

export const dropPlaceholderIconStyles = (theme: Theme, isDragOver: boolean) => css`
  font-size: 1.15rem;
  margin-bottom: 10px;
  color: ${isDragOver ? theme.palette.primary.main : alpha(theme.palette.text.primary, 0.5)};
`

export const dropPlaceholderTitleStyles = (theme: Theme) => css`
  margin: 0;
  font-size: 1.05rem;
  font-weight: 500;
  line-height: 1.35;
  color: ${alpha(theme.palette.text.primary, 0.82)};
`

export const dropPlaceholderDescriptionStyles = (theme: Theme) => css`
  margin: 8px 0 0;
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${alpha(theme.palette.text.primary, 0.58)};
`

export const columnStyles = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
  padding: ${theme.spacing(2)};
  background-color: ${theme.palette.white.main};
  border-radius: ${theme.spacing(1.5)};
`

export const columnHeaderStyles = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 0 10px;
  min-height: 104px;
`

export const columnHeaderTextStyles = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`

export const columnTitleStyles = css`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.25;
  min-height: 2.5em;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`

export const columnTitleLinkStyles = css`
  display: block;
  min-width: 0;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
`

export const columnSubtitleStyles = css`
  margin: 4px 0 0;
  font-size: 0.82rem;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.72);
`

export const closeButtonStyles = css`
  flex-shrink: 0;
  margin-top: -2px;
`

export const sectionBlockStyles = css`
  display: grid;
  grid-template-columns: 1fr;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  padding: 12px 0;
  gap: 10px;
`

export const sectionHeaderStyles = css`
  min-width: 0;
`

export const sectionContentStyles = css`
  min-width: 0;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.18) transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.18);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 0, 0, 0.28);
  }

  &[data-align-section='context-fit'] {
    height: 76px;
    overflow-y: auto;
    padding-right: 4px;
  }

  &[data-align-section='guidance'] {
    height: 300px;
    overflow-y: auto;
    padding-right: 4px;
  }

  &[data-align-section='scenario-coverage'] {
    height: 88px;
    overflow-y: auto;
    padding-right: 4px;
  }

  &[data-align-section='data-quality'] {
    height: 420px;
    overflow-y: auto;
    padding-right: 4px;
  }

  &[data-align-section='ownership-access'] {
    height: 240px;
    overflow-y: auto;
    padding-right: 4px;
  }
`

export const sectionHeadingStyles = css`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.5);
`

export const fieldLabelStyles = css`
  margin: 0 0 8px;
  font-size: 0.86rem;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.78);
`

export const mutedValueStyles = css`
  margin: 0;
  font-size: 0.86rem;
  line-height: 1.45;
  color: rgba(0, 0, 0, 0.48);
  font-style: italic;
`

export const markdownCellStyles = css`
  font-size: 0.95rem;
  line-height: 1.55;
  color: rgba(0, 0, 0, 0.82);
  min-width: 0;

  p,
  ul,
  ol {
    margin: 0;
  }

  ul,
  ol {
    padding-left: 18px;
  }

  > * + * {
    margin-top: 10px;
  }
`

export const stackedBlocksStyles = css`
  display: flex;
  flex-direction: column;
  gap: 14px;
  justify-content: flex-start;
  padding-bottom: 2px;
`

export const infoBlockStyles = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const metaLabelStyles = css`
  margin: 0;
  font-size: 0.78rem;
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 0.46);
`

export const noteBadgeStyles = (tone: 'success' | 'warning' | 'neutral') => css`
  display: inline-block;
  max-width: 100%;
  padding: 8px 12px;
  border-radius: 7px;
  font-size: 0.9rem;
  line-height: 1.55;
  overflow-wrap: anywhere;
  background: ${tone === 'success'
    ? 'rgba(86, 177, 140, 0.18)'
    : tone === 'warning'
      ? 'rgba(225, 199, 152, 0.42)'
      : 'rgba(226, 206, 163, 0.52)'};
  color: ${tone === 'success' ? '#226d56' : tone === 'warning' ? '#70470d' : '#6b4c1f'};

  p {
    margin: 0;
  }
`

export const ownerBlockStyles = css`
  display: flex;
  flex-direction: column;
  gap: 3px;
`

export const ownerNameStyles = css`
  margin: 0;
  font-size: 0.98rem;
  font-weight: 500;
  line-height: 1.35;
  color: rgba(0, 0, 0, 0.86);
`

export const ownerOrgStyles = css`
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.4;
  color: rgba(0, 0, 0, 0.62);
`

export const ownerEmailStyles = css`
  font-size: 0.86rem;
  line-height: 1.35;
  width: fit-content;
`

export const publicLinkBadgeStyles = css`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  min-height: 30px;
  padding: 6px 12px;
  border-radius: 7px;
  font-size: 0.86rem;
  font-weight: 500;
  line-height: 1.2;
  background: rgba(86, 177, 140, 0.18);
  color: #226d56;
`

export const panelRootStyles = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  position: relative;
`

export const useCasesStyles = css`
  padding: 10px 16px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  flex-shrink: 0;
  background-color: ${theme.palette.white.main};
`

export const useCaseChipStyles = css`
  cursor: pointer;
`

export const columnsGridStyles = (
  theme: Theme,
  pinnedCount: number,
  showPlaceholder: boolean,
) => css`
  flex: 1;
  overflow: auto;
  padding: 16px;
  display: grid;
  background-color: ${theme.palette.neutral.light};
  grid-template-columns: ${pinnedCount === 0
    ? '1fr'
    : showPlaceholder
      ? `repeat(${pinnedCount}, minmax(0, 1fr)) minmax(220px, 280px)`
      : `repeat(${pinnedCount}, minmax(0, 1fr))`};
  gap: 16px;
  align-items: start;
`

export const emptyStateStyles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 8px;
  text-align: center;
  background-color: ${theme.palette.primary.main};
`

export const emptyStateTextStyles = (theme: Theme) => css`
  color: ${theme.palette.text.disabled};
`
